import { Injectable } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';
import {
  MicrosoftAuthUrlResponse,
  MicrosoftCallbackRequest,
  MicrosoftCallbackResponse,
  MicrosoftConnectedAccount,
  MicrosoftDriveFilesQuery,
  MicrosoftDriveItemDto,
  MicrosoftFilePreviewItem,
  MicrosoftIntegrationStatus,
  MicrosoftMailMessageDto,
  MicrosoftMailMessagesQuery,
  MicrosoftSyncProcess,
  MicrosoftSyncStatus,
  MicrosoftSyncType,
} from '../models/microsoft-integration.model';
import { ApiService } from './api.service';
import { FrontendLoggerService } from './frontend-logger.service';

export const MICROSOFT_MALFORMED_STATUS_MESSAGE = 'Malformed Microsoft integration status response.';

type UnknownRecord = Record<string, unknown>;

@Injectable({ providedIn: 'root' })
export class MicrosoftIntegrationService {
  constructor(
    private readonly api: ApiService,
    private readonly frontendLogger: FrontendLoggerService
  ) {}

  getAuthUrl(redirectUri = this.getRedirectUri()): Observable<MicrosoftAuthUrlResponse> {
    return this.api.get<MicrosoftAuthUrlResponse>('microsoft/auth-url', { redirectUri });
  }

  exchangeCodeForToken(request: MicrosoftCallbackRequest): Observable<MicrosoftCallbackResponse> {
    const body: MicrosoftCallbackRequest = {
      code: request.code,
      redirectUri: request.redirectUri,
    };

    if (request.state) {
      body.state = request.state;
    }

    if (request.scope) {
      body.scope = request.scope;
    }

    return this.api.post<MicrosoftCallbackResponse>('microsoft/callback', body).pipe(
      catchError((error: unknown) => {
        this.frontendLogger.error('[MicrosoftIntegrationService] callback exchange failed', this.sanitizeError(error));
        return throwError(() => error);
      })
    );
  }

  getStatus(): Observable<MicrosoftIntegrationStatus> {
    return this.api.get<unknown>('microsoft/status').pipe(
      map((response) => this.mapStatusResponse(response))
    );
  }

  getSharePointPreview(): Observable<MicrosoftFilePreviewItem[]> {
    return this.api.get<MicrosoftFilePreviewItem[]>('microsoft/sharepoint/preview');
  }

  getMailMessages(query: MicrosoftMailMessagesQuery = {}): Observable<MicrosoftMailMessageDto[]> {
    return this.api.get<MicrosoftMailMessageDto[]>('microsoft/mail/messages', query);
  }

  getDriveFiles(query: MicrosoftDriveFilesQuery = {}): Observable<MicrosoftDriveItemDto[]> {
    return this.api.get<MicrosoftDriveItemDto[]>('microsoft/drive/files', query);
  }

  disconnectAccount(accountId: string): Observable<unknown> {
    return this.api.post('microsoft/disconnect', { accountId });
  }

  setDefaultAccount(accountId: string): Observable<unknown> {
    return this.api.post('microsoft/set-default', { accountId });
  }

  reauthorizeAccount(accountId: string, redirectUri = this.getRedirectUri()): Observable<MicrosoftAuthUrlResponse> {
    return this.api.post<MicrosoftAuthUrlResponse>('microsoft/reauthorize', { accountId, redirectUri });
  }

  updateSyncPeriod(accountId: string, days: number, syncType: MicrosoftSyncType = 'all'): Observable<unknown> {
    return this.api.post('microsoft/sync-period', { accountId, days, syncType });
  }

  updateSyncStartDate(accountId: string, startDate: string, syncType: MicrosoftSyncType = 'all'): Observable<unknown> {
    return this.api.post('microsoft/sync-period', { accountId, startDate, syncType });
  }

  private getRedirectUri(): string {
    return `${globalThis.location?.origin ?? ''}/oauth/microsoft/callback`;
  }

  private mapStatusResponse(response: unknown): MicrosoftIntegrationStatus {
    if (!this.isRecord(response)) {
      throw new Error(MICROSOFT_MALFORMED_STATUS_MESSAGE);
    }

    const accountsValue = this.read(response, 'accounts');
    if (!Array.isArray(accountsValue)) {
      throw new Error(MICROSOFT_MALFORMED_STATUS_MESSAGE);
    }

    const accounts = accountsValue.map((account) => this.mapAccount(account));
    if (accounts.some((account) => !account.accountId)) {
      throw new Error(MICROSOFT_MALFORMED_STATUS_MESSAGE);
    }

    return {
      isConnected: this.asBoolean(this.read(response, 'isConnected')),
      defaultAccountId: this.asNullableString(this.read(response, 'defaultAccountId')),
      accounts,
      grantedScopes: this.asStringArray(this.read(response, 'grantedScopes')),
      syncProcess: this.mapSyncProcess(this.read(response, 'syncProcess')),
    };
  }

  private mapAccount(value: unknown): MicrosoftConnectedAccount {
    if (!this.isRecord(value)) {
      throw new Error(MICROSOFT_MALFORMED_STATUS_MESSAGE);
    }

    return {
      accountId: this.asString(this.read(value, 'accountId')),
      email: this.asNullableString(this.read(value, 'email')),
      displayName: this.asNullableString(this.read(value, 'displayName')),
      tenantId: this.asNullableString(this.read(value, 'tenantId')),
      avatarUrl: this.asNullableString(this.read(value, 'avatarUrl')),
      tokenStatus: this.asTokenStatus(this.read(value, 'tokenStatus')),
      syncStatus: this.asSyncStatus(this.read(value, 'syncStatus')),
      syncPeriodDays: this.asNullableNumber(this.read(value, 'syncPeriodDays')),
      scopes: this.asStringArray(this.read(value, 'scopes')),
      isDefault: this.asBoolean(this.read(value, 'isDefault')),
      lastSyncAt: this.asNullableString(this.read(value, 'lastSyncAt')),
      lastUpdatedAt: this.asNullableString(this.read(value, 'lastUpdatedAt')),
    };
  }

  private mapSyncProcess(value: unknown): MicrosoftSyncProcess | null {
    if (value === null || value === undefined) {
      return null;
    }

    if (!this.isRecord(value)) {
      return null;
    }

    return {
      mailSyncStatus: this.asSyncStatus(this.read(value, 'mailSyncStatus')),
      driveSyncStatus: this.asSyncStatus(this.read(value, 'driveSyncStatus')),
      lastMailSyncAt: this.asNullableString(this.read(value, 'lastMailSyncAt')),
      lastDriveSyncAt: this.asNullableString(this.read(value, 'lastDriveSyncAt')),
      lastSyncDate: this.asNullableString(this.read(value, 'lastSyncDate')),
      emailsProcessed: this.asNullableNumber(this.read(value, 'emailsProcessed')),
      syncPeriod: this.asNullableNumber(this.read(value, 'syncPeriod')),
    };
  }

  private read(record: UnknownRecord, camelKey: string): unknown {
    const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
    return record[camelKey] ?? record[pascalKey];
  }

  private isRecord(value: unknown): value is UnknownRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private asNullableString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  private asStringArray(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  }

  private asBoolean(value: unknown): boolean {
    return value === true;
  }

  private asNullableNumber(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private asTokenStatus(value: unknown): MicrosoftConnectedAccount['tokenStatus'] {
    return value === 'valid' || value === 'expired' || value === 'revoked' || value === 'missing'
      ? value
      : 'unknown';
  }

  private asSyncStatus(value: unknown): MicrosoftSyncStatus {
    return value === 'idle' || value === 'pending' || value === 'running' || value === 'failed' || value === 'completed'
      ? value
      : 'unknown';
  }

  private sanitizeError(error: unknown): string {
    const status = this.isRecord(error) ? error['status'] : undefined;
    const message = this.isRecord(error) && typeof error['message'] === 'string' ? error['message'] : 'Microsoft request failed.';
    return JSON.stringify({ status: this.safeScalar(status), message: this.redactSensitiveText(message) });
  }

  private safeScalar(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value) : 'unknown';
  }

  private redactSensitiveText(value: string): string {
    return value
      .replaceAll(/([?&](?:code|state|scope|access_token|refresh_token|id_token|client_secret)=)[^&\s]+/gi, '$1[REDACTED]')
      .replaceAll(/("(?:code|state|scope|access_token|refresh_token|id_token|client_secret)"\s*:\s*")[^"]*(")/gi, '$1[REDACTED]$2');
  }
}
